import type { ReactNode } from 'react';
import type { MetricBasis, MetricWindow } from '@elutide/shared';
import { AgeStamp, BasisBadge } from './Provenance';
import { Button } from './Primitives';
import type { AgeClass } from '@/lib/format';

export interface CardProps {
  title?: string;
  action?: ReactNode;
  /** Provenance for everything inside the card. */
  basis?: MetricBasis;
  window?: MetricWindow | null;
  partial?: boolean;
  asOf?: string | null;
  ageClass?: AgeClass;
  /** Card level failure: the last data dims, one line of crit text, a Retry button. */
  error?: string | null;
  onRetry?: () => void;
  children: ReactNode;
  id?: string;
}

/**
 * The container everything sits in. 16px padding on desktop, 12px on phone, a border and a
 * lighter surface for elevation. Never a shadow.
 */
export function Card({
  title,
  action,
  basis,
  window: metricWindow,
  partial,
  asOf,
  ageClass = 'live',
  error,
  onRetry,
  children,
  id,
}: CardProps) {
  const dim = Boolean(error);
  return (
    <section className={`o-card${dim ? ' o-card--dim' : ''}`} id={id} aria-labelledby={title ? `${id ?? title}-title` : undefined}>
      {title || action || basis || asOf ? (
        <header className="o-card__head">
          {title ? (
            <h2 className="o-card__title" id={`${id ?? title}-title`}>
              {title}
            </h2>
          ) : null}
          {basis ? (
            <BasisBadge
              basis={basis}
              window={metricWindow ?? null}
              partial={partial ?? false}
            />
          ) : null}
          {asOf !== undefined ? <AgeStamp asOf={asOf} klass={ageClass} /> : null}
          {action}
        </header>
      ) : null}

      {error ? (
        <p className="o-card__error" role="status">
          <span>{error}</span>
          {onRetry ? (
            <Button size="sm" variant="ghost" onClick={onRetry}>
              Retry
            </Button>
          ) : null}
        </p>
      ) : null}

      <div className="o-card__body">{children}</div>
    </section>
  );
}
